// Arquivo: src/components/CopilotDrawer.tsx  (colar o conteúdo INTEGRAL no Code Mode)
// Drawer lateral do RDR Copilot (tema escuro). Usa ask-rdr-copilot, que só recebe
// agregados autorizados do snapshot ativo; respostas citam run_id e data de corte.

import { useEffect, useRef, useState } from 'react'
import { dashboardApi } from '@/lib/dashboardApi'

interface ChatMessage {
  role: 'user' | 'bot'
  content: string
}

const SUGESTOES = [
  'Quanto Vargem Grande tem de custo comprometido até hoje?',
  'Quais são os insumos que mais compramos?',
  'Resumo de custos da obra Campo do Pires',
  'Qual o avanço físico medido das obras?',
]

// Markdown mínimo e seguro: escapa HTML e aplica negrito/código/listas/títulos.
function formatMarkdown(raw: string): string {
  const escaped = raw
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  const lines = escaped.split(/\r?\n/)
  const html: string[] = []
  let inTable = false
  const closeTable = () => { if (inTable) { html.push('</tbody></table>'); inTable = false } }
  for (const line of lines) {
    const trimmed = line.trim()
    if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
      const cells = trimmed.slice(1, -1).split('|').map(cell => cell.trim())
      if (cells.every(cell => /^:?-{2,}:?$/.test(cell))) continue
      if (!inTable) { html.push('<table><tbody>'); inTable = true }
      html.push(`<tr>${cells.map(cell => `<td>${cell}</td>`).join('')}</tr>`)
      continue
    }
    closeTable()
    if (!trimmed) { html.push('<div style="height:6px"></div>'); continue }
    if (trimmed.startsWith('### ') || trimmed.startsWith('## ')) { html.push(`<div style="font-weight:700;margin:6px 0 2px">${trimmed.replace(/^#+\s/, '')}</div>`); continue }
    if (trimmed === '---') { html.push('<hr style="border-color:rgba(148,163,184,.25)" />'); continue }
    if (/^[-*]\s/.test(trimmed)) { html.push(`<div>• ${trimmed.replace(/^[-*]\s/, '')}</div>`); continue }
    html.push(trimmed)
  }
  closeTable()
  return html.join('\n')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*]+)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
}

export function CopilotDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [input, setInput] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const bodyRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = bodyRef.current.scrollHeight
  }, [messages, busy])

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose() }
    if (open) window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  const send = async (question: string) => {
    const text = question.trim()
    if (!text || busy) return
    setError('')
    setMessages(prev => [...prev, { role: 'user', content: text }])
    setInput('')
    setBusy(true)
    try {
      const response = await dashboardApi.askCopilot(text)
      setMessages(prev => [...prev, { role: 'bot', content: response.reply || 'Sem resposta do Copilot.' }])
    }
    catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Falha ao consultar o Copilot.')
    }
    finally {
      setBusy(false)
    }
  }

  return (
    <>
      <div className="drawer-backdrop" onClick={onClose} />
      <aside className="drawer" role="dialog" aria-label="RDR Copilot">
        <div className="drawer-header">
          <div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <span className="drawer-title">RDR Copilot</span>
              <span className="drawer-pill">Sienge Oficial</span>
            </div>
            <div className="drawer-sub">Assistente de Engenharia, Custos & Insumos</div>
          </div>
          <button type="button" className="drawer-close" onClick={() => { setMessages([]); setError(''); onClose() }} aria-label="Fechar">✕</button>
        </div>
        <div className="drawer-body" ref={bodyRef}>
          {!messages.length && !busy ? (
            <>
              <div className="msg bot">Como posso te ajudar hoje? Consulte custos, insumos, orçamentos e avanços com dados auditados do snapshot ativo.</div>
              <div className="chip-row">
                {SUGESTOES.map(sugestao => (
                  <button type="button" key={sugestao} className="chip" onClick={() => void send(sugestao)}>{sugestao}</button>
                ))}
              </div>
            </>
          ) : null}
          {messages.map((message, index) => (
            message.role === 'user'
              ? <div key={index} className="msg user">{message.content}</div>
              : <div key={index} className="msg bot" dangerouslySetInnerHTML={{ __html: formatMarkdown(message.content) }} />
          ))}
          {busy ? <div className="msg bot typing-dots"><span /><span /><span /></div> : null}
          {error ? <div className="msg bot" style={{ background: 'rgba(220,38,38,.15)' }}>{error}</div> : null}
        </div>
        <div className="drawer-input">
          <textarea
            value={input}
            rows={1}
            placeholder="Pergunte sobre custos, insumos, orçamentos ou avanços..."
            onChange={event => setInput(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter' && !event.shiftKey) {
                event.preventDefault()
                void send(input)
              }
            }}
          />
          <button type="button" className="drawer-send" disabled={busy} onClick={() => void send(input)}>Enviar</button>
        </div>
        <div className="drawer-disclaimer">Respostas auditadas com dados oficiais do Sienge e Prevision do snapshot ativo. O Copilot cita run_id e data de corte e não inventa valores.</div>
      </aside>
    </>
  )
}
